import { isClaimOrderLevel, isClaimItemLevel } from '@helpers';
import useFileUploader from '@pulse/composables/useFileUploader';
import { DISPLAY_ORDER_RESOLUTIONS, SalesChannel } from '@pulse/constants/claims';
import ClaimService from '@pulse/services/ClaimService';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import map from 'lodash/map';
import { defineStore } from 'pinia';
import { computed, ref, reactive } from 'vue';

import { useOrdersStore } from '~/store/orders';

export const DEFAULT_CLAIM = {
  category: null,
  customer: {},
  fulfillments: {},
  id: '',
  idempotencyKey: '',
  orderId: '',
  resolution: {
    desiredMethod: null,
    // ENG-6073. How to settle items the platform says we cannot send, kept apart from
    // `desiredMethod` because reship can never be the answer for something out of stock.
    // `reshipIfRestocked` decides what happens *instead*, and only if it turns out to be
    // available when the claim is approved — it never changes what they are owed.
    unavailable: {
      desiredMethod: null,
      // The items the customer was actually asked about, recorded when they were asked. Live
      // availability only reaches the orders store while the resolution page is open, so deriving
      // this at submit time dropped the whole override on a resume, and stamped items in
      // fulfillments the customer never saw.
      itemIds: [],
      reshipIfRestocked: true,
    },

    updatedShippingAddress: null,
  },
  signature: '',
  sourceOrderId: '',
};

export const useClaimStore = defineStore('conciergeClaim', () => {
  const state = reactive({
    claimSession: null,
  });
  // Had to change claim to a ref so that the subscription in claim-session.js plugin can detect changes with reactive it wasn't picking up every change
  const config = useRuntimeConfig();
  const apiBaseUrl = config.public.apiBaseUrl;
  // Clone so the exported DEFAULT_CLAIM is never mutated by store operations; mutations
  // to nested objects (e.g., fulfillments) would otherwise persist across store instances.
  const claim = ref(cloneDeep(DEFAULT_CLAIM));
  const order = useOrdersStore();
  const currentShipmentId = ref(null);

  // Create services - these can be overridden in tests
  let claimService = new ClaimService(apiBaseUrl);
  const { uploadFiles } = useFileUploader(config.public.apiBaseUrl, config.public.storage.googleBucketName);

  const requireCustomerSignature = ref(false);

  /**
   * Returns the claim's idempotency key, generating one on first use. The key lives on the
   * claim object so it persists with the form state (via the claim-session sync plugin) —
   * back-button, refresh, and double-click resubmits all carry the same key and are
   * deduplicated server-side by `IdempotencyInterceptor`.
   */
  function ensureIdempotencyKey() {
    if (!claim.value.idempotencyKey) {
      claim.value.idempotencyKey =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
    }

    return claim.value.idempotencyKey;
  }

  async function findClaimById(id) {
    return claimService.findClaimById(id);
  }

  async function findClaimSession(orderId) {
    const session = await claimService.findClaimSession(orderId);
    state.claimSession = session;

    if (session.metadata?.claim) {
      setClaim(session.metadata.claim);
    }

    if (session.metadata?.currentShipmentId) {
      currentShipmentId.value = session.metadata.currentShipmentId;
    }

    return session;
  }

  async function updateClaimSession(data) {
    const updatedSession = await claimService.updateClaimSession(data);
    state.claimSession = updatedSession;

    if (updatedSession.metadata?.claim && data.metadata?.claim) {
      setClaim(data.metadata.claim);
    }

    if (updatedSession.metadata?.currentShipmentId && data.metadata?.currentShipmentId) {
      currentShipmentId.value = data.metadata.currentShipmentId;
    }

    return updatedSession;
  }

  async function getClaimSubtypes() {
    return claimService.getClaimSubtypes(order.currentOrder.order.storeId);
  }

  async function findReviews(claimId) {
    return claimService.findReviews(claimId);
  }

  async function createReview(review, claimId) {
    return claimService.createReview(review, claimId);
  }

  async function deleteClaimSession() {
    if (!state.claimSession) return;

    await claimService.deleteClaimSession(state.claimSession.id);
    state.claimSession = null;
    claim.value = DEFAULT_CLAIM;
    currentShipmentId.value = null;
  }

  async function sendClaimMessage(content, sourceOrderId, customerId) {
    if (!claim.value._data || !claim.value._data.id) {
      throw new Error('Claim ID not found');
    }

    let medias = [];
    if (content.files.length > 0) {
      medias = await uploadFiles(content.files);
    }

    const claimId = claim.value._data.id;
    const storeId = claim.value._data.storeId;
    const senderName = claim.value._data.authorName;

    const claimMessageInput = {
      claimId,
      medias,
      messageContent: content.messageContent,
      senderId: customerId,
      senderName,
      senderType: 'customer',
      storeId,
    };

    await createClaimMessage(claimMessageInput, claim.value._data.storeId, sourceOrderId);

    const newMessages = await loadClaimMessages(claimId, sourceOrderId);

    claim.value._data.claimMessages = newMessages;
  }

  async function createClaimMessage(data, storeId, sourceOrderId) {
    return claimService.createClaimMessage(data, storeId, sourceOrderId);
  }

  async function loadClaimMessages(claimId, sourceOrderId) {
    return claimService.loadClaimMessages(claimId, sourceOrderId);
  }

  function addFulfillmentIssues(id, issues) {
    // Remove duplicate issues by sourceItemId to prevent duplicate claim items
    const seenSourceItemIds = new Set();
    const uniqueIssues = issues.filter(issue => {
      // Keep items without sourceItemId - these are custom added items (e.g., warranty items) that can't be deduplicated
      if (!issue.sourceItemId) return true;

      // Keep only the first occurrence of each sourceItemId
      if (seenSourceItemIds.has(issue.sourceItemId)) return false;

      seenSourceItemIds.add(issue.sourceItemId);

      return true;
    });

    const issuesWithIds = uniqueIssues.map(issue => ({
      ...issue,
      id: Date.now().toString(36) + Math.random().toString(36).substr(2),
    }));

    if (!claim.value.fulfillments[id]) {
      claim.value.fulfillments[id] = {
        id: id,
        issues: issuesWithIds,
      };
    } else {
      /**
       * Remove the issues for the sourceItemId that are already in the store.
       */
      claim.value.fulfillments[id].issues = filter(
        claim.value.fulfillments[id].issues,
        ({ sourceItemId }) => !map(uniqueIssues, 'sourceItemId').includes(sourceItemId),
      );

      /**
       * Add the new issues for that sourceItemId to the store.
       */
      claim.value.fulfillments[id].issues = claim.value.fulfillments[id].issues.concat(issuesWithIds);
    }
  }

  function setClaim(claimData) {
    claim.value = {
      ...claim.value,
      ...claimData,
    };
  }

  function setFulfillmentState(fulfillments) {
    fulfillments.forEach(id => {
      claim.value.fulfillments[id] = {
        id,
        issues: [],
      };
    });
  }

  function setCurrentShipmentId(id) {
    currentShipmentId.value = id;
  }

  function setRequestedResolution(resolution) {
    claim.value.resolution.desiredMethod = resolution;
  }

  function setUnavailableResolution({ desiredMethod, itemIds, reshipIfRestocked }) {
    const current = claim.value.resolution.unavailable ?? {};

    claim.value.resolution.unavailable = {
      desiredMethod: desiredMethod ?? current.desiredMethod ?? null,
      itemIds: itemIds ?? current.itemIds ?? [],
      reshipIfRestocked: reshipIfRestocked ?? current.reshipIfRestocked ?? true,
    };
  }

  /**
   * Items the platform told us we cannot send, keyed by the id the issues carry.
   *
   * Reads `inStock === false` only: null means the platform could not answer, and treating that as
   * out of stock is the failure ENG-6074 exists to stop.
   */
  const unavailableItemIds = computed(() => {
    const live = order.currentOrder?.orderItems ?? [];

    return new Set(live.filter(item => item.inStock === false).map(item => item.sourceItemId));
  });

  /** Only what the customer was actually shown and answered carries a settlement of its own. */
  const settledUnavailableIds = computed(() => new Set(claim.value.resolution.unavailable?.itemIds ?? []));

  function setClaimCategory(category) {
    claim.value.category = category;
  }

  function setShippingAddress(address) {
    claim.value.resolution.updatedShippingAddress = address;
  }

  function setShipmentIssueType(id, type) {
    const fulfillment = claim.value.fulfillments[id];

    claim.value.fulfillments[id] = {
      ...fulfillment,
      shipmentIssue: type,
    };
  }

  function setShipmentIssueSubtype(id, subIssueId) {
    const fulfillment = claim.value.fulfillments[id];

    claim.value.fulfillments[id] = {
      ...fulfillment,
      shipmentIssueSubtype: subIssueId,
    };
  }

  function setShipmentIssueDetails(id, details) {
    claim.value.fulfillments[id].details = details;
  }

  function addSignature(imageUrl) {
    claim.value.signature = imageUrl;
  }

  function saveSignature() {
    const data = {
      claimId: claim.value.id,
      imageUrl: claim.value.signature,
      orderId: claim.value.orderId,
    };

    claimService.saveCustomerSignature(data.claimId, data);
  }

  function deleteIssueByFulfillmentAndIssue(fulfillmentId, issueId) {
    const fulfillment = claim.value.fulfillments[fulfillmentId];
    if (!fulfillment) return;

    fulfillment.issues = fulfillment.issues.filter(issue => issue.id !== issueId);
  }

  function clearIssuesByFulfillment(fulfillmentId) {
    if (claim.value.fulfillments[fulfillmentId]) {
      claim.value.fulfillments[fulfillmentId].issues = [];
    }
  }

  function getIssuesByFulfillmentAndProduct(fulfillmentId, sourceItemId) {
    const fulfillment = claim.value.fulfillments[fulfillmentId];
    if (!fulfillment || !fulfillment.issues) return [];

    return fulfillment.issues.filter(issue => issue.sourceItemId === sourceItemId);
  }

  // Order-wide lookups for warranty, whose items span fulfillments (no single "current shipment").
  function getIssuesByProduct(sourceItemId) {
    return Object.values(claim.value.fulfillments)
      .flatMap(fulfillment => fulfillment.issues ?? [])
      .filter(issue => issue.sourceItemId === sourceItemId);
  }

  function deleteIssueById(issueId) {
    Object.keys(claim.value.fulfillments).forEach(fulfillmentId => {
      const fulfillment = claim.value.fulfillments[fulfillmentId];
      if (fulfillment?.issues) {
        fulfillment.issues = fulfillment.issues.filter(issue => issue.id !== issueId);
      }
    });
  }

  function transformStoreToClaim(email, claimInfo) {
    const firstFulfillment = Object.values(claimInfo.fulfillments)[0];

    return {
      claimCategory: claim.value.category,
      email,
      fulfillmentIssues: Object.entries(claimInfo.fulfillments).reduce((acc, [sourceFulfillmentId, fulfillment]) => {
        const unavailable = claimInfo.resolution.unavailable ?? {};

        const issues = fulfillment.issues.map(
          // eslint-disable-next-line no-unused-vars
          ({ item: _unusedItem, ...issue }) => {
            // Read from what was recorded when the customer answered, never recomputed from live
            // availability here (ENG-6073). Deriving it at submit time stamped items in shipments
            // they never saw, sent a preference nobody was offered when the block did not render,
            // and dropped the override entirely on a resume, when live data is gone.
            if (!settledUnavailableIds.value.has(issue.sourceItemId)) return { ...issue };

            return {
              ...issue,
              reshipIfRestocked: !!unavailable.reshipIfRestocked,
              resolutionRequested: unavailable.desiredMethod ?? claimInfo.resolution.desiredMethod,
            };
          },
        );

        acc.push({
          issues,
          sourceFulfillmentId,
        });

        return acc;
      }, []),
      resolution: {
        customerSignature: claimInfo.signature,
        desiredMethod: claimInfo.resolution.desiredMethod,
        updatedShippingAddress: claimInfo.resolution.updatedShippingAddress,
        warrantyPeriod:
          order.currentOrder?.eligibility?.[firstFulfillment.issues[0]?.sourceItemId]?.warranty?.validity ?? null,
      },
      salesChannel: SalesChannel.ONLINE,
    };
  }

  function submitClaim() {
    const createClaimData = {
      claim: transformStoreToClaim(claim.value.customer.email, claim.value),
      sourceOrderId: claim.value.customer.sourceOrderId,
      ...claim.value.customer,
      // Must come after the customer spread so it can't be shadowed by an unexpected
      // `idempotencyKey` field on the customer object.
      idempotencyKey: ensureIdempotencyKey(),
    };

    return claimService.createClaim(createClaimData);
  }

  function notifyCustomer(claimId) {
    return claimService.notifyClaimCustomer(claimId);
  }

  function setCustomer({ email, sourceOrderId, customerName }) {
    const customer = {
      customerName,
      email,
      sourceOrderId,
    };

    claim.value = { ...DEFAULT_CLAIM, ...claim.value, customer };
  }

  function reset() {
    // Deep clone so nested objects (fulfillments, resolution) don't share references
    // with the exported DEFAULT_CLAIM — mutating them would otherwise leak across resets.
    claim.value = cloneDeep(DEFAULT_CLAIM);
  }

  /**
   * @typedef {Object} FoundationFulfillment
   * @property {string} id
   * @property {Array.<Object>} issues
   *
   * @returns {Array.<FoundationFulfillment>}
   */
  const unfinishedFulfillments = computed(() => {
    return Object.values(claim.value.fulfillments).filter(({ issues }) => issues.length === 0);
  });

  const isOrderLevel = computed(() => {
    return isClaimOrderLevel(claim.value);
  });

  const isItemLevel = computed(() => {
    return isClaimItemLevel(claim.value);
  });

  const claimCategory = computed(() => {
    return claim.value.category;
  });

  const messages = computed(() => {
    if (!claim?.value?._data?.claimMessages) {
      return [];
    }

    const messages = claim?.value?._data?.claimMessages;

    const result = messages.map(message => {
      const formattedDate = new Date(message.createdAt).toLocaleDateString('en-US');

      return {
        ...message,
        date: formattedDate,
        // image: 'https://picsum.photos/200',
        key: message.id,
        medias: message.medias || [],
        message: message.messageContent,
        senderType: message.senderType,
        type: 'message',
        username: message.senderName,
      };
    });

    return result;
  });

  const resolution = computed(() => {
    return claim.value.resolution ? claim.value.resolution : null;
  });

  const currentShipment = computed(() => {
    return claim.value.fulfillments[currentShipmentId.value];
  });

  const currentShipmentIssues = computed(() => {
    return currentShipment.value?.issues || [];
  });

  const allIssues = computed(() => {
    return Object.values(claim.value.fulfillments).reduce((all, { issues }) => all.concat(issues), []);
  });

  const requestedResolution = computed(() => DISPLAY_ORDER_RESOLUTIONS[claim.value.resolution.desiredMethod]);

  const claimSession = computed(() => state.claimSession);

  return {
    addFulfillmentIssues,
    addSignature,
    allIssues,
    claim,
    claimCategory,
    claimService,
    claimSession,
    clearIssuesByFulfillment,
    createClaimMessage,
    createReview,
    currentShipment,
    currentShipmentId,
    currentShipmentIssues,
    deleteClaimSession,
    deleteIssueByFulfillmentAndIssue,
    deleteIssueById,
    findClaimById,
    findClaimSession,
    findReviews,
    getClaimSubtypes,
    getIssuesByFulfillmentAndProduct,
    getIssuesByProduct,
    isItemLevel,
    isOrderLevel,
    loadClaimMessages,
    messages,
    notifyCustomer,
    requestedResolution,
    requireCustomerSignature,
    reset,
    resolution,
    saveSignature,
    sendClaimMessage,
    setClaim,
    setClaimCategory,
    setCurrentShipmentId,
    setCustomer,
    setFulfillmentState,
    setRequestedResolution,
    setShipmentIssueDetails,
    setShipmentIssueSubtype,
    setShipmentIssueType,
    setShippingAddress,
    setUnavailableResolution,
    submitClaim,
    unavailableItemIds,
    unfinishedFulfillments,
    updateClaimSession,
  };
});
